function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useState, useEffect, forwardRef } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import { Button } from '../Button';
import { Close } from '../Close';
import { Heading } from '../Heading';
import { ESCAPE_KEYCODE, ESCAPE_KEY, HEADER_VARIANTS, BUTTON_VARIANTS, SIZES } from '../Utils/constant';
import useIsMobile from '../Utils/customHooks/useIsMobile';
import moduleStyles from '@dtsl/css-modal-dialog/dist/index.module.css';

// eslint-disable-next-line react/display-name
const ModalDialog = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    children,
    isVisible = false,
    closeModal = () => {},
    size = SIZES.MEDIUM
  } = props;
  const [showModal, setShowModal] = useState(isVisible);
  const {
    isMobile
  } = useIsMobile(768);
  const onAnimationEnd = () => {
    if (!isVisible) ReactDOM.flushSync(() => {
      setShowModal(false);
    });
  };
  const isFooterPresent = children.some(elt => elt?.props?.primaryAction || elt?.props?.secondaryAction);
  const uniqueId = `${new Date().getTime()}`;
  useEffect(() => {
    const handleEscKey = event => {
      if (event.keyCode === ESCAPE_KEYCODE || event.key === ESCAPE_KEY) {
        isVisible && closeModal(event);
      }
    };
    if (isVisible) {
      setShowModal(true);
      document.body.classList.add(moduleStyles['sib-modal-dialog-active']);
    } else {
      document.body.classList.remove(moduleStyles['sib-modal-dialog-active']);
    }
    document.addEventListener('keydown', handleEscKey, false);
    return () => {
      document.removeEventListener('keydown', handleEscKey, false);
    };
  }, [isVisible]);
  const modalDialogWrapperClasses = classNames({
    [moduleStyles['sib-modal-dialog']]: true,
    [moduleStyles['sib-modal-dialog--medium']]: !!size && size === SIZES.MEDIUM,
    [moduleStyles['sib-modal-dialog--small']]: !!size && size === SIZES.SMALL
  });
  const backdropClasses = classNames({
    [moduleStyles['sib-modal-dialog-backdrop']]: true
  });
  return showModal && /*#__PURE__*/ReactDOM.createPortal(/*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: modalDialogWrapperClasses,
    "aria-modal": "true",
    role: "dialog",
    ref: ref,
    "aria-describedby": `body-${uniqueId}`,
    "aria-labelledby": `title-${uniqueId}`,
    onAnimationEnd: onAnimationEnd,
    style: {
      animation: `${isVisible ? isMobile ? 'var(--sib-transition_slide-modal-to-top, slide-modal-to-top 350ms ease-in)' : 'var(--sib-transition_fade-in-modal, fade-in-modal 350ms ease-in-out)' : isMobile ? 'var(--sib-transition_slide-modal-to-bottom, 350ms slide-modal-to-bottom ease-in-out)' : 'var(--sib-transition_fade-out-modal, 350ms fade-out-modal ease-in-out)'}`
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: moduleStyles['sib-modal-dialog-content']
  }, React.Children.map(children, (child, index) => {
    return /*#__PURE__*/React.cloneElement(child, {
      key: index,
      isFooterPresent,
      uniqueId,
      closeModal
    });
  }))), /*#__PURE__*/React.createElement("div", {
    className: backdropClasses,
    onAnimationEnd: onAnimationEnd,
    style: {
      animation: `${isVisible ? 'var(--sib-transition_backdrop-open-modal, backdrop-open 350ms ease-in-out)' : 'var(--sib-transition_backdrop-close-modal, backdrop-close 350ms ease-in-out)'}`
    }
  })), document.body);
});
ModalDialog.Header = props => {
  const {
    closeModal = () => {},
    title = '',
    headerVariant = HEADER_VARIANTS.DEFAULT,
    uniqueId: id,
    closeButtonProps = {}
  } = props;
  const {
    className = '',
    ariaLabel = 'close button',
    ...restProps
  } = closeButtonProps;
  const headerClasses = classNames({
    [moduleStyles['sib-modal-dialog__header']]: true,
    [`${moduleStyles[`sib-modal-dialog__header--${HEADER_VARIANTS.ACCENT}`]}`]: !!headerVariant && headerVariant === HEADER_VARIANTS.ACCENT
  });
  const closeButtonClasses = classNames({
    [moduleStyles['modal-dialog-close-btn']]: true,
    className: className
  });
  return /*#__PURE__*/React.createElement("header", {
    className: headerClasses,
    id: `title-${id}`
  }, /*#__PURE__*/React.createElement(Heading, {
    className: moduleStyles['modal-title'],
    fontFamily: "primary"
  }, title), /*#__PURE__*/React.createElement(Close, _extends({
    className: closeButtonClasses,
    onClick: event => closeModal(event),
    ariaLabel: ariaLabel
  }, restProps)));
};
ModalDialog.Body = props => {
  const {
    children,
    isFooterPresent,
    uniqueId: id,
    className = ''
  } = props;
  const bodyClasses = classNames({
    [moduleStyles['sib-modal-dialog__body']]: true,
    [moduleStyles['sib-modal-dialog__body--no-footer']]: !isFooterPresent,
    [className]: !!className
  });
  const shadowClasses = classNames({
    [moduleStyles['sib-modal-dialog__box-shadow']]: true,
    [moduleStyles['sib-modal-dialog__box-shadow--no-footer']]: !isFooterPresent
  });
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: bodyClasses,
    id: `body-${id}`
  }, children), /*#__PURE__*/React.createElement("span", {
    className: shadowClasses
  }));
};
ModalDialog.Footer = props => {
  const {
    secondaryAction = null,
    primaryAction = null
  } = props;
  return /*#__PURE__*/React.createElement("footer", {
    className: moduleStyles['sib-modal-dialog__footer']
  }, secondaryAction ? /*#__PURE__*/React.createElement(Button, secondaryAction) : null, primaryAction ? /*#__PURE__*/React.createElement(Button, primaryAction) : null);
};
ModalDialog.propTypes = {
  isVisible: PropTypes.bool,
  closeModal: PropTypes.func,
  size: PropTypes.oneOf(Object.values(SIZES)),
  children: PropTypes.node.isRequired
};
ModalDialog.Header.propTypes = {
  title: PropTypes.string,
  headerVariant: PropTypes.oneOf(Object.values(HEADER_VARIANTS)),
  closeModal: PropTypes.func,
  uniqueId: PropTypes.string,
  closeButtonProps: PropTypes.object
};
ModalDialog.Body.propTypes = {
  children: PropTypes.node,
  isFooterPresent: PropTypes.bool,
  uniqueId: PropTypes.string,
  className: PropTypes.string
};
ModalDialog.Footer.propTypes = {
  secondaryAction: PropTypes.shape({
    variant: PropTypes.oneOf(Object.values(BUTTON_VARIANTS)),
    label: PropTypes.string,
    iconAlone: PropTypes.bool,
    icon: PropTypes.node,
    ellipsis: PropTypes.bool,
    disabled: PropTypes.bool,
    tooltip: PropTypes.bool,
    tooltipValue: PropTypes.object,
    dropdownOpen: PropTypes.bool,
    showArrowIcon: PropTypes.bool,
    className: PropTypes.string
  }),
  primaryAction: PropTypes.shape({
    variant: PropTypes.oneOf(Object.values(BUTTON_VARIANTS)),
    label: PropTypes.string,
    iconAlone: PropTypes.bool,
    icon: PropTypes.node,
    ellipsis: PropTypes.bool,
    disabled: PropTypes.bool,
    tooltip: PropTypes.bool,
    tooltipValue: PropTypes.object,
    dropdownOpen: PropTypes.bool,
    showArrowIcon: PropTypes.bool,
    className: PropTypes.string
  })
};
ModalDialog.Header.displayName = 'ModalDialogHeader';
ModalDialog.Body.displayName = 'ModalDialogBody';
ModalDialog.Footer.displayName = 'ModalDialogFooter';
export default ModalDialog;